/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Profile
 * File     : Profile.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Profile view and coordinating data fetching.
 * 
 * Dependencies:
 *   - ../components/layout/RatingToasts
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material
 *   - @mui/material
 *   - react
 *   - react-router-dom
 * 
 * Usage:
 *   Use in Router: <Route path="/profile" element={<Profile />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateMe, getRatingOpportunities, getMyVotes, getMyComments, listClubs, listClubMembersPublic, removeClubMember, api } from '../services/api';
import { Select, MenuItem, Alert as MuiAlert } from '@mui/material';
import { Box, Button, Card, CardContent, Divider, Grid, Stack, TextField, Typography, List, ListItem, ListItemText, Chip, Link as MuiLink, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useLocation } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import { Link } from 'react-router-dom';

const Countdown = ({ target }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const ms = Math.max(0, target - now);
  const s = Math.floor(ms / 1000);
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return <Chip color="info" label={`In ${hh}:${mm}:${ss}`} size="small" />;
};

const Profile = () => {
  const { user } = useAuth();
  const location = useLocation();

  const [form, setForm] = useState({ displayName: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [opportunities, setOpportunities] = useState([]);
  const [votes, setVotes] = useState([]);
  const [comments, setComments] = useState([]);
  const [clubs, setClubs] = useState([]);
  const maxClubs = useMemo(() => (user?.role === 'owner' ? Infinity : 5), [user]);
  const requiresHostPlan = useMemo(() => (!!user && user.role !== 'owner' && user.planStatus !== 'active'), [user]);
  const ownedCount = useMemo(() => clubs.filter(c => c.ownerUserId === user?.id).length, [clubs, user]);
  const [clubMembers, setClubMembers] = useState({});
  const [showWelcome, setShowWelcome] = useState(false);
  const [tierChoice, setTierChoice] = useState(0);
  const isOwner = user?.role === 'owner';
  const isHostActive = isOwner || user?.planStatus === 'active';

  useEffect(() => {
    if (user) setForm(f => ({ ...f, displayName: user.displayName || '', email: user.email || '' }));
  }, [user]);
  useEffect(() => {
    const t = Number(user?.clubSlotsTier || 0);
    setTierChoice(t);
  }, [user?.clubSlotsTier]);

  // Show welcome modal if redirected after signup
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setShowWelcome(true);
      // Clean the query to avoid re-show on refresh
      const url = new URL(window.location.href);
      url.searchParams.delete('new');
      window.history.replaceState({}, '', url.toString());
    }
  }, [location.search]);

  useEffect(() => {
    if (!user) return;
    getRatingOpportunities().then(r => setOpportunities(r.opportunities || [])).catch(() => setOpportunities([]));
    getMyVotes().then(r => setVotes(r.votes || [])).catch(() => setVotes([]));
    getMyComments().then(r => setComments(r.comments || [])).catch(() => setComments([]));
    listClubs().then(r => setClubs(r.clubs || [])).catch(() => setClubs([]));
  }, [user]);

  useEffect(() => {
    const fetchMembers = async () => {
      const map = {};
      for (const c of clubs) {
        try {
          const r = await listClubMembersPublic(c.id);
          map[c.id] = r.members || [];
        } catch (_) { map[c.id] = []; }
      }
      setClubMembers(map);
    };
    if (clubs.length > 0) fetchMembers();
  }, [clubs]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg('');
    if (form.password && !window.isSecureContext) {
      setSaveMsg('Password change is only allowed over HTTPS.');
      setSaving(false);
      return;
    }
    try {
      await updateMe({ displayName: form.displayName || null, email: form.email || undefined, password: form.password || undefined });
      setSaveMsg('Saved');
      setForm(f => ({ ...f, password: '' }));
    } catch (_) {
      setSaveMsg('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return <Typography>Please log in to view your profile.</Typography>;

  return (
    <Stack spacing={3} sx={{ px: { xs: 1, sm: 0 } }}>
      <Dialog open={showWelcome} onClose={() => setShowWelcome(false)} fullWidth maxWidth="sm">
        <DialogTitle>Welcome to ReelConsensus! Set your Display Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Your <strong>Display Name</strong> is how others will see you in the ReelConsensus: comments, poll creator labels,
            top poll creators, club creator names, results pages, and vote pages.
          </Typography>
          <Typography variant="body2">
            If you leave it blank, we’ll show your <strong>username</strong> instead. You’ll still sign in with your username.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowWelcome(false)}>Got it</Button>
          <Button variant="contained" onClick={() => { setShowWelcome(false); }}>Start Editing</Button>
        </DialogActions>
      </Dialog>
      <Typography variant="h4">Profile</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Account</Typography>
              <Box component="form" onSubmit={onSubmit} noValidate>
                <Stack spacing={2}>
                  <TextField label="Username" value={user.username} InputProps={{ readOnly: true }} fullWidth />
                  <TextField label="Display Name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="How your name appears to others" fullWidth />
                  <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" fullWidth />
                  <TextField label="New Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="password" fullWidth />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button type="submit" variant="contained" disabled={saving} fullWidth={true}>Save</Button>
                    {saveMsg && <Typography variant="body2" color={saveMsg === 'Saved' ? 'success.main' : 'error.main'} sx={{ mt: { xs: 1, sm: 0 } }}>{saveMsg}</Typography>}
                  </Stack>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Next Rating Opportunity</Typography>
              {opportunities.length === 0 ? (
                <Typography variant="body2">No rating opportunities yet.</Typography>
              ) : (
                <List>
                  {opportunities.filter(op => op && op.pollId).map(op => (
                    <ListItem key={op.pollId} disableGutters secondaryAction={<Countdown target={new Date(op.eligibleAt).getTime()} />} sx={{ pr: { xs: 0, sm: 6 } }}>
                      <ListItemText
                        primary={<MuiLink component={Link} to={`/results/${op.pollId}`}>{op.movieTitle}</MuiLink>}
                        secondary={`Poll: ${op.pollTitle}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Subscription</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Status: {isOwner ? 'Owner' : (user?.planStatus || 'none')}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Select size="small" value={tierChoice} onChange={(e) => setTierChoice(Number(e.target.value))} sx={{ minWidth: 220 }} disabled={!isHostActive} MenuProps={{ disableScrollLock: true }}>
              <MenuItem value={0}>Tier: None (Host: up to 5)</MenuItem>
              <MenuItem value={1}>Tier 1 (6–9)</MenuItem>
              <MenuItem value={2}>Tier 2 (10–19)</MenuItem>
              <MenuItem value={3}>Tier 3 (20–29)</MenuItem>
            </Select>
            <Button variant="contained" disabled={!isHostActive} onClick={async () => {
              try {
                const tier = Number(tierChoice);
                if (![1,2,3].includes(tier)) {
                  alert('Select Tier 1, 2, or 3 to purchase.');
                  return;
                }
                const r = await api.post('/billing/add-slots', { quantity: tier });
                if (r?.data?.url) window.location.href = r.data.url;
              } catch (e) {
                const msg = e?.response?.data?.error || 'Unable to start tier checkout';
                alert(msg);
              }
            }}>Update Tier</Button>
            {user?.planStatus === 'active' ? (
              <Button variant="text" color="error" onClick={async () => {
                if (!window.confirm('Cancel Host subscription at period end? Clubs remain but new polls will be locked.')) return;
                try { await api.post('/billing/cancel'); alert('Subscription will cancel at period end.'); } catch (e) { alert(e?.response?.data?.error || 'Cancel failed'); }
              }}>Cancel Host</Button>
            ) : null}
          </Stack>
          {!isHostActive && (
            <MuiAlert severity="warning">Your Host plan is inactive. Clubs remain visible but new poll creation is locked until you resubscribe.</MuiAlert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>My Clubs {Number.isFinite(maxClubs) ? `( ${clubs.filter(c => c.ownerUserId === user.id).length} / ${maxClubs} used )` : '(Unlimited)'}</Typography>
          {requiresHostPlan && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip color="warning" size="small" icon={<LockIcon fontSize="small" />} label={`Host plan required to create clubs (${ownedCount} / 5 used)`} />
              <Button size="small" onClick={async () => { try { const r = await api.post('/billing/checkout'); if (r?.data?.url) window.location.href = r.data.url; } catch (_) {} }}>Subscribe</Button>
            </Stack>
          )}
          {clubs.length === 0 ? (
            <Typography variant="body2">You are not in any clubs.</Typography>
          ) : (
            <List>
              {clubs.filter(c => c && c.id).map(c => (
                <React.Fragment key={c.id}>
                  <ListItem disableGutters secondaryAction={
                    ((user.role === 'owner' || user.role === 'admin' || c.ownerUserId === user.id || (clubMembers[c.id] || []).some(m => m.userId === user.id && m.role === 'admin')) && (clubMembers[c.id]?.length === 0)) ? (
                      <Button color="error" variant="text" size="small" onClick={async () => {
                        if (window.confirm('Delete this club? This cannot be undone.')) {
                          try { await fetch(`${process.env.REACT_APP_API_URL || '/api'}/clubs/${c.id}`, { method: 'DELETE', headers: { 'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '' } }); window.location.reload(); } catch (_) {}
                        }
                      }}>Delete Club</Button>
                    ) : null
                  }>
                    <ListItemText primary={<MuiLink component={Link} to={`/clubs/${c.id}`}>{c.name}</MuiLink>} secondary={`Owner: ${c.owner?.username || 'Unknown'}`} />
                  </ListItem>
                  <Box sx={{ pl: 2, pb: 1 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {(clubMembers[c.id] || []).map(m => (
                        <Chip key={m.userId} label={m.username} size="small" onDelete={(user.role === 'owner' || user.role === 'admin' || (c.ownerUserId === user.id) || ((clubMembers[c.id] || []).some(x => x.userId === user.id && x.role === 'admin') && m.userId !== c.ownerUserId)) ? async () => {
                          if (window.confirm(`Remove ${m.username} from ${c.name}?`)) {
                            try { await removeClubMember(c.id, m.userId); setClubMembers(cm => ({ ...cm, [c.id]: (cm[c.id] || []).filter(x => x.userId !== m.userId) })); } catch (_) {}
                          }
                        } : undefined} />
                      ))}
                    </Stack>
                  </Box>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>My Votes</Typography>
              {votes.length === 0 ? <Typography variant="body2">No votes yet.</Typography> : (
                <List>
                  {votes.filter(v => v && v.id).map(v => (
                    <ListItem key={v.id} disableGutters>
                      <ListItemText
                        primary={<MuiLink component={Link} to={`/results/${v.pollId}`}>{v.movie?.title || 'Movie'}</MuiLink>}
                        secondary={`Poll: ${v.poll?.title || v.pollId}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>My Comments</Typography>
              {comments.length === 0 ? <Typography variant="body2">No comments yet.</Typography> : (
                <List>
                  {comments.filter(c => c && c.id).map(c => (
                    <ListItem key={c.id} disableGutters>
                      <ListItemText
                        primary={c.message}
                        secondary={<MuiLink component={Link} to={`/clubs/${c.club?.id || ''}`}>{c.club?.name || 'Club'}</MuiLink>}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default Profile;


